import { useEffect, useRef, useState } from "react";
import { featuresApi } from "../api/featuresApi";
import "../../../V2SideDrawer.css";

export default function ExcelImportDrawer({ onClose, onImported, showToast }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleFileChange = (e) => {
    setError("");
    setResult(null);
    const f = e.target.files?.[0] ?? null;
    setFile(f);
  };

  const downloadTemplate = () => {
    window.open(featuresApi.excelTemplateUrl(), "_blank");
  };

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    setError("");
    setResult(null);
    try {
      const data = await featuresApi.uploadExcel(file);
      setResult(data);
      const createdTotal = (data?.created_features || 0) + (data?.created_sub_features || 0);
      if (createdTotal > 0) {
        showToast?.(`Imported ${createdTotal} record${createdTotal === 1 ? "" : "s"}`, "success");
        onImported?.();
      } else {
        showToast?.("Upload processed — no new records created", "warn");
      }
    } catch (err) {
      setError(err?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <div className="v2drw-scrim" onClick={onClose} />
      <div className="v2drw ftv2-drw" role="dialog" aria-modal="true">
        <div className="v2drw-head">
          <div className="v2drw-head-main">
            <div className="v2drw-eyebrow">Features</div>
            <div className="v2drw-title">Import Excel</div>
          </div>
          <button className="v2drw-close" onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2" fill="none">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {error && <div className="v2drw-msg warn">{error}</div>}

        <div className="v2drw-body">
          <div className="ftv2-form">
            <div className="ftv2-form-fld">
              <label className="ftv2-form-label">Step 1 — Download the template</label>
              <div>
                <button type="button" className="ftv2-import-link" onClick={downloadTemplate}>
                  ↓ Download Excel template
                </button>
                <div className="ftv2-import-hint">
                  Two sheets: Features and Sub-features. `product_name` must match an existing product exactly.
                </div>
              </div>
            </div>

            <div className="ftv2-form-fld">
              <label className="ftv2-form-label">Step 2 — Upload your file</label>
              <div className="ftv2-import-row">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xls,.xlsx"
                  className="dfv2-att-file-input"
                  onChange={handleFileChange}
                />
                <button
                  type="button"
                  className="dfv2-att-add-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  {file ? "Change File" : "Choose File"}
                </button>
                <span className="dfv2-att-file-name">{file ? file.name : "No file selected"}</span>
              </div>
            </div>

            {result && (
              <div className="ftv2-import-result">
                <div className="ftv2-import-result-row">
                  <span className="ftv2-import-result-k">Features created</span>
                  <span className="ftv2-import-result-v">{result.created_features ?? 0}</span>
                </div>
                <div className="ftv2-import-result-row">
                  <span className="ftv2-import-result-k">Sub-features created</span>
                  <span className="ftv2-import-result-v">{result.created_sub_features ?? 0}</span>
                </div>
                {Array.isArray(result?.errors) && result.errors.length > 0 && (
                  <div className="ftv2-import-result-row">
                    <span className="ftv2-import-result-k">Errors</span>
                    <span className="ftv2-import-result-v">{result.errors.length}</span>
                  </div>
                )}
                {Array.isArray(result?.errors) && result.errors.length > 0 && (
                  <ul className="ftv2-import-errors">
                    {result.errors.slice(0, 12).map((e, i) => (
                      <li key={i}>{typeof e === "string" ? e : (e?.message || JSON.stringify(e))}</li>
                    ))}
                    {result.errors.length > 12 && <li>… and {result.errors.length - 12} more</li>}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="v2drw-foot">
          <span className="v2drw-foot-spacer" />
          <button className="v2drw-btn-cancel" onClick={onClose} disabled={uploading}>Close</button>
          <button className="v2drw-btn-save" onClick={upload} disabled={uploading || !file}>
            {uploading ? "Uploading…" : "Upload"}
          </button>
        </div>
      </div>
    </>
  );
}
